<script lang="ts">
  // libs
  import { createEventDispatcher } from "svelte";
  import Flash from "@shared/services/flashv2.js";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { get } from "svelte/store";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import {
    getStockSiteFulfillmentData,
    getFulfillmentInformation,
  } from "@retailer/services/order_fulfillment_service";
  import { loadRetailerUserAuthentications } from "@retailer/services/retailer_user_authentications_service";

  // components
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import { download } from "@shared/svelte/Modules/Download.svelte";
  import { restrictRepAccess } from "@retailer/services/preview_forbidden_flash";
  import OrderFulfillmentModal from "@retailer/svelte/Cart/Page/OrderFulfillment/OrderFulfillmentModal.svelte";
  import OrderReviewSection from "@retailer/svelte/Cart/Page/OrderReviewSection.svelte";
  import ExpandedOrderSummary from "@retailer/svelte/Cart/Page/Components/ExpandedOrderSummary.svelte";

  // stores
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";
  import PunchoutSessionStore from "@retailer/svelte/stores/PunchoutSessionStore";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";

  // types
  import type {
    CartType,
    FulfillmentInformationType,
    ManagerType,
    CartStockSiteType,
    CartOrderType,
  } from "@shared/types/Cart";
  import { CartOrderGroupEntity } from "@shared/services/event_tracking/entities";

  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  // props
  export let Cart;
  export let cart: CartType;
  export let managers: ManagerType[];
  export let showLoading: boolean;
  export let submitDisabled: boolean = false;
  export let managersForSubmit = {};

  const dispatch = createEventDispatcher();

  // data
  let spinSubmitButton = false;
  let submitting = false;
  let showOrderFulfillmentModal = false;
  let fulfillmentInformationByDistributor: FulfillmentInformationType[];
  let stockSiteData;
  const { trackClick } = EventTracking;
  const orderReviewContainerClasses =
    "fizz-flex-item fizz-stack-16 fizz-surface fizz-radius-medium fizz-surface-gray fizz-inset-square-12";

  const closeOrderFulfillmentModal = () => {
    showOrderFulfillmentModal = false;
  };

  const showExpandedOrderSummary = stagedRolloutCheck("expanded_order_summary");
  const OHLQOrderPaymentMethodsEnabled = stagedRolloutCheck(
    "marketplace__ohlq_order_payment_methods",
  );
  const retailerUserAuthRollout = stagedRolloutCheck("marketplace__retailer_user_authentication");

  let wineCellarPickupAndDeliveryOrder: CartOrderType;

  const submit = async () => {
    let needsAuthCheck =
      retailerUserAuthRollout &&
      cart.orders.some(o => {
        const distributor = get(DistributorsStore).find(d => d.id === o.distributor.id);
        return distributor?.require_retailer_user_authentication;
      });
    if (needsAuthCheck) {
      spinSubmitButton = true;
      await loadRetailerUserAuthentications(true);
      await Cart.load();
      spinSubmitButton = false;
    }
    if (restrictRepAccess()) {
      return;
    } else if (await cartCannotSubmit()) {
      submissionWithErrors();
      Cart.Flash.setMessage("Resolve all errors to submit this order", true, "error");
    } else if (hasFulfillmentDateOrders()) {
      spinSubmitButton = true;
      if (pickupStockSites || deliveryStockSites) {
        fulfillmentInformationByDistributor = getFulfillmentInformation(
          wineCellarPickupAndDeliveryOrder,
          deliveryStockSites,
          pickupStockSites,
        );
        spinSubmitButton = false;
        showOrderFulfillmentModal = true;
      } else {
        Cart.getFulfillmentInformation((res: { data: FulfillmentInformationType[] }) => {
          fulfillmentInformationByDistributor = res.data;
          spinSubmitButton = false;
          showOrderFulfillmentModal = true;
        });
      }
    } else {
      submitting = true;
      showLoading = true;

      try {
        await Cart.submit();
      } catch (err) {
        submitting = false;
        showLoading = false;

        if (err?.data instanceof Array) {
          err?.data.forEach((message: string) => {
            Flash.now({ type: "error", compact: true, message });
          });
        } else {
          Flash.now({ type: "error", compact: true, message: "An unexpected error occurred." });
        }
      }
    }
  };
  const submissionWithErrors = () => {
    dispatch("submitWithErrors");
  };

  const submitForApproval = async () => {
    if (restrictRepAccess()) {
      return;
    }

    var managerIds = [];
    for (const id in managersForSubmit) {
      if (managersForSubmit[id]) managerIds.push(id);
    }
    if (managerIds.length === 0) {
      alert("Please choose which managers to submit to.");
      return;
    }
    if (await cartCannotSubmit()) {
      Flash.now({
        type: "error",
        compact: true,
        message: "Resolve all errors to submit this order",
      });
      submissionWithErrors();
      return;
    }
    submitting = true;
    Cart.submitForApproval(managerIds, (err, res) => {
      submitting = false;
      if (res) {
        cart = res;
        Cart.Flash.setMessage("Your cart has been submitted to your managers.", true, "notice");
      } else if (err) {
        Cart.Flash.setMessage(
          "There was an error submitting for approval. Please try again later.",
          true,
          "error",
        );
      }
    });
  };

  const submitForPunchout = async () => {
    if (restrictRepAccess()) {
      return;
    }

    if (await cartCannotSubmit()) {
      Flash.now({
        type: "error",
        compact: true,
        message: "Resolve all errors to submit this order",
      });

      submissionWithErrors();
      return;
    }

    submitting = true;

    try {
      const result = await Cart.submitForPunchout();

      if (result.data.order_message) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = result.data.browser_form_post_url;

        const input = document.createElement("input");
        input.type = "hidden";
        input.value = result.data.order_message;
        input.name = "cxml-urlencoded";

        form.appendChild(input);
        document.body.appendChild(form);

        await HttpPromise({
          method: "DELETE",
          url: "/api/retailer/user",
        });

        form.submit();
      } else {
        Flash.now({ type: "error", compact: true, message: "An unexpected error occurred." });
      }
    } catch (err) {
      submitting = false;
      showLoading = false;

      if (err?.data instanceof Array) {
        err?.data.forEach((message: string) => {
          Flash.now({ type: "error", compact: true, message });
        });
      } else {
        Flash.now({ type: "error", compact: true, message: "An unexpected error occurred." });
      }
    }
  };

  const downloadWrapper = (id: number) => {
    return () => {
      trackClick("cart_download", [new CartOrderGroupEntity({ orderGroupId: cart.id })]);
      download(id);
    };
  };

  // reactives
  $: cartCannotSubmit = async () => {
    await Cart.load();
    return submitting || !cart.orders.length || !Cart.canSubmit();
  };
  $: cartIsEmpty = !submittableOrders?.length;
  $: orderWarehouses = cart?.orders?.reduce((warehouses, order) => {
    return order?.distributor?.uses_warehouse_pickup ? order?.order_warehouses : warehouses;
  }, []);
  $: hasFulfillmentDateOrders = () => {
    return (
      orderWarehouses?.length > 0 &&
      cart?.orders?.some(
        order =>
          order?.distributor?.uses_warehouse_pickup &&
          order?.distributor?.id !== VT_802_SPIRITS_DISTRIBUTOR_ID,
      )
    );
  };
  $: submittableOrders = cart?.orders?.filter(order => !order.distributor?.ordering_disabled);
  $: cartOrderCountText = pluralize("order", submittableOrders?.length, true);
  $: {
    wineCellarPickupAndDeliveryOrder = cart.orders.find(
      order =>
        order.distributor?.uses_wine_cellar &&
        order.distributor?.uses_warehouse_pickup &&
        order.distributor?.id !== VT_802_SPIRITS_DISTRIBUTOR_ID,
    );
    if (OHLQOrderPaymentMethodsEnabled && wineCellarPickupAndDeliveryOrder) {
      stockSiteData = getStockSiteFulfillmentData(wineCellarPickupAndDeliveryOrder);
    }
  }
  $: pickupStockSites =
    !$stockSiteData || $stockSiteData?.loading || $stockSiteData?.error
      ? null
      : (($stockSiteData.data?.pickupStockSites?.nodes || []) as CartStockSiteType[]);
  $: deliveryStockSites =
    !$stockSiteData || $stockSiteData?.loading || $stockSiteData?.error
      ? null
      : (($stockSiteData.data?.deliveryStockSites?.nodes || []) as CartStockSiteType[]);
</script>

<div
  class="fizz-radius-medium fizz-surface"
  class:fizz-inset-square-24={$LayoutStore.isDesktop}
  data-testid="submit-box"
>
  <div class="fizz-card">
    {#if $LayoutStore.isDesktop}
      <div class="fizz-border-bottom fizz-surface fizz-stack-16">
        <div class="fizz-flex-row fizz-space-between fizz-stack-16">
          <h4 class="fizz-heading-4">Order Summary</h4>
          <button
            class="fizz-button fizz-button-small"
            name="download"
            on:click={downloadWrapper(cart.id)}
            disabled={cartIsEmpty}
          >
            Download
          </button>
        </div>
      </div>
    {/if}
    {#if showExpandedOrderSummary && !cartIsEmpty}
      <ExpandedOrderSummary {cart} />
    {/if}
    {#if cartIsEmpty}
      <div class="fizz-text-strong fizz-text-large fizz-text-center fizz-stack-12">
        No orders to send
      </div>
    {/if}
    {#if $LayoutStore.isMobile}
      <OrderReviewSection
        {managers}
        {managersForSubmit}
        {cart}
        containerClasses={orderReviewContainerClasses}
      />
    {/if}
    <div
      class:fizz-inset-horizontal-16={$LayoutStore.isMobile}
      class:fizz-stack-16={$LayoutStore.isMobile}
    >
      {#if !showExpandedOrderSummary}
        <div class="fizz-stack-12">
          <div id="total-items" class="fizz-flex-row fizz-space-between fizz-stack-8">
            <h4 class="fizz-heading-4">Total items</h4>
            <p>{pluralize("item", cart.total_items, true)}</p>
          </div>
          <div id="total-items-breakdown">
            {#if !!cart.total_pickup_items}
              <ul style="list-style: none;">
                <li id="total-pickup-items" class="fizz-flex-row fizz-space-between fizz-stack-8">
                  <h4 class="fizz-heading-4">Items for pickup</h4>
                  <p>{pluralize("item", cart.total_pickup_items, true)}</p>
                </li>
                <li id="total-delivery-items" class="fizz-flex-row fizz-space-between fizz-stack-8">
                  <h4 class="fizz-heading-4">Items for delivery</h4>
                  <p>{pluralize("item", cart.total_delivery_items, true)}</p>
                </li>
              </ul>
            {/if}
          </div>
        </div>
        <div class="fizz-flex-row fizz-space-between fizz-stack-24">
          <h4 class="fizz-heading-4">Total distributors</h4>
          <p>{pluralize("distributor", cart.orders.length, true)}</p>
        </div>
      {/if}
      {#if $CurrentRetailerStore?.has_punchout}
        {#if $PunchoutSessionStore}
          {#if !$PunchoutSessionStore.is_active_punchout}
            <div class="fizz-alert-error fizz-alert--small fizz-stack-12"
              ><p
                >You must first punchout through {$PunchoutSessionStore.procurement_system_name} to submit
                an order.
                <a href={$PunchoutSessionStore.link} rel="noopener noreferrer" target="_blank"
                  >Go to {$PunchoutSessionStore.procurement_system_name}.</a
                ></p
              ></div
            >
          {/if}

          <button
            class="fizz-button-secondary fizz-button-large fizz-full-width fizz-stack-12 submit-button"
            class:fizz-button-spinner={spinSubmitButton}
            disabled={showLoading ||
              submitDisabled ||
              cartIsEmpty ||
              !$PunchoutSessionStore.is_active_punchout}
            on:click={submitForPunchout}
          >
            {#if submitting}
              Processing...
            {:else}
              Send {cartOrderCountText} to {$PunchoutSessionStore.procurement_system_name}
            {/if}
          </button>
          <p class="fizz-text-subdued"
            >You will be redirected to {$PunchoutSessionStore.procurement_system_name} to submit your
            order for approval. Orders must be approved within {$PunchoutSessionStore.hours_expiration}
            hours of submission.</p
          >
        {:else}
          <div class="fizz-alert-error fizz-alert--small fizz-stack-12"
            ><p>Unexpected error finding your Punchout session.</p>
          </div>
        {/if}
      {:else}
        <Authorization authName={"carts.submit"}>
          <button
            data-testid="submit-orders-button"
            class="fizz-button-secondary fizz-button-large fizz-full-width submit-button"
            class:fizz-button-spinner={spinSubmitButton}
            disabled={showLoading || submitDisabled || cartIsEmpty}
            on:click={submit}
          >
            {#if submitting}
              Processing...
            {:else}
              Send {cartOrderCountText}
            {/if}
          </button>
        </Authorization>
        <Authorization authName={"carts.submit_approval"}>
          <button
            class="fizz-button-secondary fizz-button-large fizz-full-width submit-button"
            on:click={submitForApproval}
            disabled={!!cart.submitted_for_approval_at || showLoading}
          >
            {#if submitting}
              Processing...
            {:else if cart.submitted_for_approval_at}
              Submitted for approval
            {:else}
              Submit for approval
            {/if}
          </button>
        </Authorization>
      {/if}
    </div>
  </div>
</div>

{#if showOrderFulfillmentModal}
  <OrderFulfillmentModal
    closeModal={closeOrderFulfillmentModal}
    {fulfillmentInformationByDistributor}
    cartSubmit={Cart.submit}
    fulfillmentUpdate={Cart.batchUpdateOrderWarehouses}
    batchUpdateOrders={Cart.batchUpdateOrders}
    {OHLQOrderPaymentMethodsEnabled}
    distributorUsesWineCellar={OHLQOrderPaymentMethodsEnabled && !!wineCellarPickupAndDeliveryOrder}
  />
{/if}
